import { useState, useEffect, useRef, useCallback } from "react";
import { siteData, WhatsAppSettings, LiveChatSettings } from "@/store/siteData";
import { submitContactForm } from "@/utils/apiClient";
import {
  startChatSession,
  sendVisitorMessage,
  pollVisitorMessages,
  visitorCloseSession,
  saveChatSession,
  loadChatSession,
  clearChatSession,
  ChatMessage,
} from "@/utils/chatApi";

// ── Bildirim Sesi ────────────────────────────────────────────────────────────

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch { /* ses desteklenmiyor */ }
}

// ── WhatsApp Button ──────────────────────────────────────────────────────────

function WhatsAppWidget({ settings }: { settings: WhatsAppSettings }) {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = () => {
    const msg = encodeURIComponent(settings.message || "Merhaba!");
    const phone = settings.phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank", "noopener");
  };

  const isRight = settings.position === "bottom-right";

  return (
    <div
      className={`fixed bottom-24 z-50 ${isRight ? "right-5" : "left-5"}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {showTooltip && (
        <div
          className={`absolute bottom-full mb-2 ${isRight ? "right-0" : "left-0"} bg-white text-gray-800 text-xs font-medium px-3 py-2 rounded-xl shadow-lg whitespace-nowrap border border-gray-100`}
          style={{ animation: "fadeInUp 0.2s ease" }}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
            {settings.label || "WhatsApp ile Yazın"}
          </div>
          <div
            className={`absolute top-full ${isRight ? "right-4" : "left-4"} w-0 h-0`}
            style={{ borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "6px solid white" }}
          ></div>
        </div>
      )}
      <button
        onClick={handleClick}
        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-all hover:scale-110 active:scale-95"
        style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </button>
    </div>
  );
}

// ── Contact Form Mode ────────────────────────────────────────────────────────

function ContactFormMode({ settings, onClose }: { settings: LiveChatSettings; onClose: () => void }) {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setSending(true);
    setSubmitError("");
    try {
      await submitContactForm({
        ad_soyad: formData.name,
        email: formData.email,
        telefon: formData.phone,
        konu: "Canlı Destek Formu",
        mesaj: formData.message,
      });
      setSent(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Mesaj gönderilemedi. Lütfen tekrar deneyin.");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="p-6 text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
          <i className="ri-check-double-line text-green-600 text-2xl"></i>
        </div>
        <p className="text-gray-900 font-semibold">Mesajınız alındı!</p>
        <p className="text-gray-500 text-sm mt-1">{settings.offlineMessage}</p>
        <button
          onClick={() => { setSent(false); setFormData({ name: "", email: "", phone: "", message: "" }); onClose(); }}
          className="mt-4 text-blue-600 text-sm underline cursor-pointer"
        >
          Tamam
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-3">
      <p className="text-gray-600 text-xs">{settings.offlineMessage}</p>
      {[
        { label: "Adınız *", key: "name", type: "text", required: true },
        { label: "E-posta *", key: "email", type: "email", required: true },
        { label: "Telefon", key: "phone", type: "tel", required: false },
      ].map(f => (
        <div key={f.key}>
          <label className="block text-gray-600 text-xs mb-1">{f.label}</label>
          <input
            type={f.type}
            required={f.required}
            value={formData[f.key as keyof typeof formData]}
            onChange={e => setFormData(p => ({ ...p, [f.key]: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 transition-colors text-gray-800"
          />
        </div>
      ))}
      <div>
        <label className="block text-gray-600 text-xs mb-1">Mesajınız *</label>
        <textarea
          required
          value={formData.message}
          onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
          rows={3}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 transition-colors resize-none text-gray-800"
        />
      </div>
      {submitError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-lg">
          <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
          <span>{submitError}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={sending}
        className="w-full py-2.5 rounded-lg text-white text-sm font-medium cursor-pointer transition-all disabled:opacity-70 whitespace-nowrap"
        style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)" }}
      >
        {sending ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...
          </span>
        ) : "Gönder"}
      </button>
    </form>
  );
}

// ── Real Live Chat Mode ───────────────────────────────────────────────────────

interface LiveChatModeProps {
  settings: LiveChatSettings;
}

function LiveChatMode({ settings }: LiveChatModeProps) {
  const existingSession = loadChatSession();

  const [phase, setPhase] = useState<"form" | "chat">(existingSession ? "chat" : "form");
  const [startForm, setStartForm] = useState({ name: "", email: "", phone: "" });
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");

  const [sessionId, setSessionId] = useState(existingSession?.sessionId ?? "");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Gerçek mesajların en son ID'sini tut (optimistic ID'ler Date.now() olduğundan büyük olabilir)
  const lastRealId = useRef(0);
  const [sessionStatus, setSessionStatus] = useState<string>("waiting");
  const [closeReason, setCloseReason] = useState<string | null>(null);
  const [inputMsg, setInputMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [ending, setEnding] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const now = () => new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Polling — sadece yeni gerçek mesajları ekler, optimistic'leri değiştirir
  const doPoll = useCallback(async (sid: string) => {
    try {
      const res = await pollVisitorMessages(sid, lastRealId.current);
      if (res.messages.length > 0) {
        const hasAgent = res.messages.some(m => m.sender === 'agent');
        if (hasAgent) playNotificationSound();
        const newRealId = res.messages[res.messages.length - 1].id;
        lastRealId.current = newRealId;
        setMessages(prev => {
          // Optimistic mesajları (id >= Date.now() tarzı büyük sayılar) filtrele
          // Gerçek sunucu mesajlarını al, sadece henüz listede olmayanları ekle
          const existingRealIds = new Set(prev.filter(m => m.id < 1e12).map(m => m.id));
          const incoming = res.messages.filter(m => !existingRealIds.has(m.id));
          if (incoming.length === 0) return prev;
          // Optimistic visitor mesajlarını çıkar (gerçeği geldi)
          const withoutOptimistic = prev.filter(m => m.id < 1e12);
          return [...withoutOptimistic, ...incoming];
        });
      }
      setSessionStatus(res.sessionStatus);
      if (res.closeReason) setCloseReason(res.closeReason);
    } catch { /* sessizce geç */ }
  }, []);

  useEffect(() => {
    if (phase !== "chat" || !sessionId) return;
    // İlk yükleme
    doPoll(sessionId);
    pollRef.current = setInterval(() => doPoll(sessionId), 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [phase, sessionId, doPoll]);

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startForm.name) return;
    setStarting(true);
    setStartError("");
    try {
      const res = await startChatSession(startForm.name, startForm.email, startForm.phone);
      saveChatSession({ sessionId: res.sessionId, visitorName: startForm.name, visitorEmail: startForm.email });
      setSessionId(res.sessionId);
      const welcomeMsg: ChatMessage = {
        id: 0,
        session_id: res.sessionId,
        sender: "agent",
        agent_name: settings.agentName,
        message: res.welcomeMessage,
        created_at: new Date().toISOString(),
      };
      lastRealId.current = 0;
      setMessages([welcomeMsg]);
      setPhase("chat");
    } catch (err) {
      setStartError(err instanceof Error ? err.message : "Bağlantı kurulamadı");
    }
    setStarting(false);
  };

  const handleSend = async () => {
    const txt = inputMsg.trim();
    if (!txt || !sessionId) return;
    setInputMsg("");
    setSending(true);
    // Optimistic (id >= 1e13 ile gerçek sunucu ID'lerinden ayırt edilir)
    const optimisticId = Date.now() + 1e13;
    const optimistic: ChatMessage = {
      id: optimisticId,
      session_id: sessionId,
      sender: "visitor",
      agent_name: null,
      message: txt,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    try {
      await sendVisitorMessage(sessionId, txt);
    } catch { /* gönderim hatası */ }
    setSending(false);
  };

  const handleEndChat = () => {
    clearChatSession();
    setPhase("form");
    setMessages([]);
    setSessionId("");
    lastRealId.current = 0;
    setCloseReason(null);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const handleVisitorClose = async () => {
    if (!sessionId) return;
    setEnding(true);
    try { await visitorCloseSession(sessionId); } catch { /* sessiz */ }
    setEnding(false);
    setShowEndConfirm(false);
    clearChatSession();
    setSessionStatus("closed");
    setCloseReason("visitor");
  };

  // ── Başlangıç Formu ──
  if (phase === "form") {
    return (
      <form onSubmit={handleStart} className="p-4 space-y-3">
        <p className="text-gray-500 text-xs">Canlı destek ekibimizle bağlantı kurmak için bilgilerinizi girin:</p>
        <div>
          <label className="block text-gray-600 text-xs mb-1">Adınız *</label>
          <input type="text" required value={startForm.name}
            onChange={e => setStartForm(p => ({ ...p, name: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 text-gray-800" />
        </div>
        <div>
          <label className="block text-gray-600 text-xs mb-1">E-posta</label>
          <input type="email" value={startForm.email}
            onChange={e => setStartForm(p => ({ ...p, email: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 text-gray-800" />
        </div>
        <div>
          <label className="block text-gray-600 text-xs mb-1">Telefon</label>
          <input type="tel" value={startForm.phone}
            onChange={e => setStartForm(p => ({ ...p, phone: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 text-gray-800" />
        </div>
        {startError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">{startError}</div>
        )}
        <button type="submit" disabled={starting}
          className="w-full py-2.5 rounded-lg text-white text-sm font-medium cursor-pointer transition-all disabled:opacity-70 whitespace-nowrap flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)" }}>
          {starting ? <><i className="ri-loader-4-line animate-spin"></i> Bağlanıyor...</> : <><i className="ri-customer-service-2-line"></i> Sohbet Başlat</>}
        </button>
      </form>
    );
  }

  // ── Chat Ekranı ──
  return (
    <>
      {sessionStatus === "waiting" && (
        <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center gap-2">
          <i className="ri-time-line text-amber-500 text-sm"></i>
          <span className="text-amber-700 text-xs">Bir temsilci bağlanmayı bekliyor...</span>
        </div>
      )}
      {sessionStatus === "closed" && (
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 space-y-1">
          {closeReason === "timeout" ? (
            <div className="flex items-center gap-2">
              <i className="ri-alarm-warning-line text-orange-400 text-sm"></i>
              <span className="text-orange-600 text-xs font-medium">İnaktivite nedeniyle sohbet kapatıldı.</span>
            </div>
          ) : closeReason === "visitor" ? (
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-500 text-sm"></i>
              <span className="text-green-700 text-xs">Sohbeti başarıyla sonlandırdınız.</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <i className="ri-information-line text-gray-400 text-sm"></i>
              <span className="text-gray-500 text-xs">Bu sohbet kapatıldı.</span>
            </div>
          )}
          <button onClick={handleEndChat} className="text-blue-600 text-xs underline cursor-pointer whitespace-nowrap block">Yeni Sohbet Başlat</button>
        </div>
      )}

      {/* Onay modalı */}
      {showEndConfirm && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3 space-y-2">
          <p className="text-red-700 text-xs font-medium">Sohbeti bitirmek istediğinize emin misiniz?</p>
          <div className="flex gap-2">
            <button
              onClick={handleVisitorClose}
              disabled={ending}
              className="flex-1 py-1.5 bg-red-500 text-white text-xs rounded-lg cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap"
            >
              {ending ? <><i className="ri-loader-4-line animate-spin"></i></> : "Evet, Bitir"}
            </button>
            <button
              onClick={() => setShowEndConfirm(false)}
              className="flex-1 py-1.5 border border-gray-300 text-gray-600 text-xs rounded-lg cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              İptal
            </button>
          </div>
        </div>
      )}

      <div className="h-64 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.map((msg, i) => (
          <div key={`${msg.id}-${i}`} className={`flex ${msg.sender === "visitor" ? "justify-end" : "justify-start"} gap-2`}>
            {msg.sender === "agent" && (
              <img src={settings.agentImageUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
            )}
            <div className={`max-w-[75%] flex flex-col gap-1 ${msg.sender === "visitor" ? "items-end" : "items-start"}`}>
              {msg.sender === "agent" && msg.agent_name && (
                <span className="text-gray-500 text-xs">{msg.agent_name}</span>
              )}
              <div className={`px-3 py-2 rounded-2xl text-sm leading-snug ${
                msg.sender === "visitor"
                  ? "bg-blue-600 text-white rounded-tr-sm"
                  : "bg-white text-gray-800 rounded-tl-sm border border-gray-100"
              }`}>
                {msg.message}
              </div>
              <span className="text-gray-400 text-xs">
                {msg.created_at ? new Date(msg.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : now()}
              </span>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef}></div>
      </div>
      {sessionStatus !== "closed" && (
        <div className="bg-white border-t border-gray-100 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputMsg}
              onChange={e => setInputMsg(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
              placeholder="Mesajınızı yazın..."
              className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:border-blue-400 transition-colors text-gray-800"
            />
            <button
              onClick={handleSend}
              disabled={sending || !inputMsg.trim()}
              className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 flex-shrink-0 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)" }}
            >
              <i className="ri-send-plane-fill text-white text-sm"></i>
            </button>
          </div>
          <button
            onClick={() => setShowEndConfirm(true)}
            className="w-full text-center text-red-400 text-xs hover:text-red-600 cursor-pointer py-0.5 transition-colors whitespace-nowrap"
          >
            <i className="ri-close-circle-line mr-1"></i>Sohbeti Bitir
          </button>
        </div>
      )}
    </>
  );
}

// ── Live Chat Widget Container ────────────────────────────────────────────────

function LiveChatWidget({ settings }: { settings: LiveChatSettings }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const isRight = (siteData.getWhatsApp().position ?? "bottom-right") === "bottom-right";
  const hasWA = siteData.getWhatsApp().enabled;
  const bottomClass = isRight ? "right-5" : "left-5";
  const bottomOffset = hasWA ? "bottom-44" : "bottom-6";

  return (
    <>
      <div className={`fixed ${bottomOffset} ${bottomClass} z-50`}>
        <button
          onClick={() => { setOpen(o => !o); if (!open) setUnread(0); }}
          className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-all hover:scale-110 active:scale-95 relative"
          style={{ background: open ? "#374151" : "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)" }}
          aria-label="Canlı Destek"
        >
          <div className="w-7 h-7 flex items-center justify-center">
            {open
              ? <i className="ri-close-line text-white text-2xl"></i>
              : <i className="ri-customer-service-2-fill text-white text-2xl"></i>
            }
          </div>
          {unread > 0 && !open && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">{unread}</span>
          )}
        </button>
      </div>

      {open && (
        <div
          className={`fixed ${bottomOffset} ${bottomClass} z-40 mb-16 w-80 sm:w-96 rounded-2xl overflow-hidden border border-gray-200`}
          style={{ animation: "slideUpIn 0.25s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center gap-3"
            style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)" }}
          >
            <div className="relative flex-shrink-0">
              <img
                src={settings.agentImageUrl}
                alt={settings.agentName}
                className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
              />
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">{settings.title}</p>
              <p className="text-blue-100 text-xs truncate">{settings.subtitle}</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white cursor-pointer w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Online badge */}
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
            <span className="text-green-700 text-xs font-medium">{settings.agentName}</span>
            <span className="text-gray-400 text-xs">— {settings.onlineHours}</span>
          </div>

          {/* Body */}
          <div className="bg-white">
            {settings.formEnabled
              ? <ContactFormMode settings={settings} onClose={() => setOpen(false)} />
              : <LiveChatMode settings={settings} />
            }
          </div>
        </div>
      )}
    </>
  );
}

// ── Root Export ──────────────────────────────────────────────────────────────

export default function ContactWidgets() {
  const [waSettings, setWaSettings] = useState<WhatsAppSettings>(siteData.getWhatsApp());
  const [lcSettings, setLcSettings] = useState<LiveChatSettings>(siteData.getLiveChat());
  // Zamanlama kontrolu: otomasyon açıksa saat çizelgesine göre online/offline
  const [scheduleOnline, setScheduleOnline] = useState(() => siteData.isChatOnlineNow());

  useEffect(() => {
    const tick = () => setScheduleOnline(siteData.isChatOnlineNow());
    const t = setInterval(tick, 60_000); // dakikada bir kontrol
    return () => clearInterval(t);
  }, []);

  const schedule = siteData.getChatSchedule();
  // Eger otomasyon aktifse saat kontrolune bak; degilse lcSettings.enabled
  const liveChatVisible = lcSettings.enabled && (schedule.autoSchedule ? scheduleOnline : true);

  useEffect(() => {
    const interval = setInterval(() => {
      setWaSettings(siteData.getWhatsApp());
      setLcSettings(siteData.getLiveChat());
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {waSettings.enabled && <WhatsAppWidget settings={waSettings} />}
      {liveChatVisible && <LiveChatWidget settings={lcSettings} />}
    </>
  );
}
